import type { LucideIcon } from "lucide-react";
import type { ReactNode } from "react";

import { cn } from "@/lib/utils";

interface StatCardProps {
  label: ReactNode;
  value: ReactNode;
  /** Texto auxiliar abaixo do valor (ex.: "últimos 30 dias"). */
  hint?: ReactNode;
  /** Icone opcional no canto superior direito. */
  icon?: LucideIcon;
  /** Estado de loading -- substitui valor por skeleton pulsante. */
  loading?: boolean;
  /**
   * - `default`: card grande com valor em 28px (KPIs do Observability/MeUsage).
   * - `compact`: card horizontal (label + valor) para listas densas.
   */
  variant?: "default" | "compact";
  className?: string;
}

export function StatCard({
  label,
  value,
  hint,
  icon: Icon,
  loading = false,
  variant = "default",
  className,
}: StatCardProps) {
  if (variant === "compact") {
    return (
      <div
        className={cn(
          "flex items-center justify-between gap-3 rounded-xl border border-line bg-canvas px-3 py-2",
          className,
        )}
      >
        <span className="text-[10px] font-semibold uppercase tracking-wide text-ink-500">
          {label}
        </span>
        {loading ? (
          <span className="h-4 w-12 animate-pulse rounded bg-ink-100" />
        ) : (
          <span className="tabular text-sm font-semibold text-ink-950">
            {value}
          </span>
        )}
      </div>
    );
  }
  return (
    <div
      className={cn(
        "shadow-card flex flex-col gap-1 rounded-2xl border border-line bg-surface p-5",
        className,
      )}
    >
      <div className="flex items-start justify-between gap-2">
        <p className="text-[10px] font-semibold uppercase tracking-[0.18em] text-ink-500">
          {label}
        </p>
        {Icon ? <Icon className="h-4 w-4 text-ink-400" aria-hidden /> : null}
      </div>
      {loading ? (
        <span className="mt-1 h-7 w-24 animate-pulse rounded bg-ink-100" />
      ) : (
        <p className="tabular text-[28px] font-semibold leading-tight text-ink-950">
          {value}
        </p>
      )}
      {hint ? <p className="text-xs text-ink-500">{hint}</p> : null}
    </div>
  );
}
